import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/learn", label: "Learn" },
  { to: "/podcast", label: "Podcast" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Insights" },
];

export function Logo({ light = false }) {
  const name = "Shubham Bapna";
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald text-white font-heading font-bold">S</span>
      <span className={`font-heading font-bold text-lg tracking-tight ${light ? "text-white" : "text-navy"}`}>{name}</span>
    </Link>
  );
}

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to)) ? "text-accent" : "text-muted hover:text-ink"}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Button data-testid="nav-apply-btn" onClick={() => navigate("/apply")}
            className="rounded-[3px] bg-brass px-4 text-sm font-semibold text-paper hover:opacity-90">Apply</Button>
          {user ? (
            <Button data-testid="nav-dashboard-btn" variant="ghost" onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
              className="text-mutedink hover:text-ink">
              {user.role === "admin" ? "Admin" : "Dashboard"} <ChevronRight className="ml-1 h-4 w-4" strokeWidth={2} />
            </Button>
          ) : (
            <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")}
              className="text-mutedink hover:text-ink">Log in</Button>
          )}
        </div>
        <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6 text-ink" /> : <Menu className="h-6 w-6 text-ink" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-background px-5 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/ /g, "-")}`}
                className="rounded-md px-3 py-2.5 text-sm font-semibold text-ink hover:bg-card">
                {l.label}
              </Link>
            ))}
            <div className="mt-3">
              <Button onClick={() => { setOpen(false); navigate("/apply"); }}
                className="mb-2 w-full rounded-[3px] bg-brass text-paper">Apply</Button>
              {user ? (
                <Button onClick={() => { setOpen(false); navigate(user.role === "admin" ? "/admin" : "/dashboard"); }}
                  className="w-full rounded-md bg-navy text-white">Go to {user.role === "admin" ? "Admin" : "Dashboard"}</Button>
              ) : (
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }} className="w-full">Log in</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
